// Motor puro del tour guiado del admin (driver.js). Sin React ni efectos:
// resolver anclas, esperar a que aparezcan, calcular dónde reanudar y
// armar el HTML del globo. Todo recibe el documento y el reloj por
// parámetro para poder probarse en jsdom (donde offsetParent siempre es null).
import { TOUR_STEPS } from './tourSteps';

export function selectorsOf(element) {
  if (!element) return [];
  return Array.isArray(element) ? element.filter(Boolean) : [element];
}

export function defaultIsVisible(el) {
  return !!el && el.offsetParent !== null;
}

// El sidebar se renderiza dos veces: el de escritorio lleva el id canónico
// (#menu-x) y el drawer móvil el prefijado (#mobile-menu-x). En pantalla
// chica el de escritorio está en display:none, así que se cae al drawer.
const mobileVariant = (selector) => (selector.startsWith('#') ? `#mobile-${selector.slice(1)}` : null);

function query(doc, selector) {
  try {
    return doc.querySelector(selector);
  } catch {
    return null;
  }
}

export function resolveAnchor(element, { doc = document, isVisible = defaultIsVisible } = {}) {
  for (const selector of selectorsOf(element)) {
    const desktop = query(doc, selector);
    if (isVisible(desktop)) return desktop;
    const mobileSelector = mobileVariant(selector);
    const mobile = mobileSelector ? query(doc, mobileSelector) : null;
    if (isVisible(mobile)) return mobile;
  }
  return null;
}

export function needsMobileMenu(element, { doc = document, isVisible = defaultIsVisible } = {}) {
  return selectorsOf(element).some((selector) => {
    if (!selector.startsWith('#menu-')) return false;
    if (isVisible(query(doc, selector))) return false;
    return !!query(doc, mobileVariant(selector));
  });
}

export function waitForAnchor(element, {
  timeout = 3000, interval = 100, doc = document, isVisible = defaultIsVisible,
  schedule = (fn, ms) => setTimeout(fn, ms), now = Date.now,
} = {}) {
  const started = now();
  return new Promise((resolve) => {
    const check = () => {
      const found = resolveAnchor(element, { doc, isVisible });
      if (found) return resolve(found);
      if (now() - started >= timeout) return resolve(null);
      return schedule(check, interval);
    };
    check();
  });
}

const defaultRectOf = (el) => el.getBoundingClientRect();
const sameRect = (a, b) => !!a && !!b
  && Math.abs(a.top - b.top) < 1 && Math.abs(a.left - b.left) < 1
  && Math.abs(a.width - b.width) < 1 && Math.abs(a.height - b.height) < 1;

// Espera a que el ancla esté lista para resaltarse: el MISMO nodo, conectado, con la misma
// posición en `stableChecks` revisiones seguidas. Así no se resalta el nodo que React está por
// reemplazar al cambiar de página, ni un drawer a medio deslizar. Al vencer el tiempo devuelve
// el último nodo conectado (o null si nunca apareció).
export function waitForStableAnchor(element, {
  timeout = 3000, interval = 80, stableChecks = 2, doc = document, isVisible = defaultIsVisible,
  rectOf = defaultRectOf, schedule = (fn, ms) => setTimeout(fn, ms), now = Date.now,
} = {}) {
  const started = now();
  let last = null;
  let lastRect = null;
  let stable = 0;
  return new Promise((resolve) => {
    const check = () => {
      const found = resolveAnchor(element, { doc, isVisible });
      if (found && found.isConnected !== false) {
        const rect = rectOf(found);
        stable = found === last && sameRect(rect, lastRect) ? stable + 1 : 0;
        last = found;
        lastRect = rect;
        if (stable >= stableChecks) return resolve(found);
      } else {
        last = null;
        lastRect = null;
        stable = 0;
      }
      if (now() - started >= timeout) return resolve(last && last.isConnected !== false ? last : null);
      return schedule(check, interval);
    };
    check();
  });
}

// Vigila el paso abierto. driver.js solo recoloca el globo con scroll o resize de la ventana; esto
// cubre lo demás: si el elemento se movió (llegaron datos, scroll del menú) recoloca; si React lo
// reemplazó, o el paso cayó al globo centrado y su ancla ya apareció, vuelve a resaltar el paso.
// Devuelve `tick`, que el contexto llama cada pocos cientos de ms.
export function createAnchorWatch({ driver, resolveFor, isBusy, rectOf = defaultRectOf }) {
  let watched = null;
  let watchedRect = null;
  return function tick() {
    if (isBusy()) return;
    const el = driver.getActiveElement();
    const idx = driver.getActiveIndex();
    if (!el || idx === undefined || idx === null) return;
    if (el.id === 'driver-dummy-element') {
      watched = null;
      if (resolveFor(idx)) driver.moveTo(idx);
      return;
    }
    if (!el.isConnected) {
      watched = null;
      driver.moveTo(idx);
      return;
    }
    const rect = rectOf(el);
    // Paso nuevo: un recálculo con el globo ya en su tamaño final (driver.js lo mide antes y en
    // celular lo encimaba sobre el elemento). Mismo paso: recalcula solo si el elemento se movió.
    if (el !== watched || !sameRect(rect, watchedRect)) driver.refresh();
    watched = el;
    watchedRect = rect;
  };
}

// current_step es el último paso COMPLETADO: se reanuda en el siguiente.
export function resumeIndex(onboarding, steps) {
  if (!onboarding || onboarding.tour_completed || !onboarding.current_step || !steps?.length) return 0;
  const idx = steps.findIndex((s) => s.id === onboarding.current_step);
  if (idx < 0) return 0;
  return Math.min(idx + 1, steps.length - 1);
}

export function stepNumberOf(role, stepId, tourSteps = TOUR_STEPS) {
  if (!stepId) return 0;
  const steps = tourSteps[role] || [];
  return steps.findIndex((s) => s.id === stepId) + 1;
}

export function popoverDescription(step, index, total) {
  const dots = Array.from({ length: total }, (_, i) => {
    const state = i === index ? ' active' : i < index ? ' done' : '';
    return `<span class="soem-tour-dot${state}"></span>`;
  }).join('');
  return `<p class="soem-tour-desc">${step.description}</p>`
    + '<div class="soem-tour-footer">'
    + `<span class="soem-tour-step-count">Paso ${index + 1} de ${total}</span>`
    + `<div class="soem-tour-dots">${dots}</div>`
    + '</div>';
}

// Evita que un doble clic en "Siguiente" avance dos pasos mientras se navega.
export function createAdvanceLock() {
  let busy = false;
  return {
    isBusy: () => busy,
    async run(fn) {
      if (busy) return undefined;
      busy = true;
      try {
        return await fn();
      } finally {
        busy = false;
      }
    },
  };
}
